export const CHUNK_ROTATE_MS = 5 * 60 * 1000;

const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

export function pickRecorderMimeType(
  isTypeSupported = (type) =>
    typeof MediaRecorder !== "undefined" &&
    typeof MediaRecorder.isTypeSupported === "function" &&
    MediaRecorder.isTypeSupported(type),
) {
  return (
    MIME_CANDIDATES.find((type) => {
      try {
        return isTypeSupported(type);
      } catch {
        return false;
      }
    }) || ""
  );
}

export function extensionForMime(mimeType) {
  const mt = String(mimeType || "").toLowerCase();
  if (mt.includes("mp4") || mt.includes("m4a") || mt.includes("aac"))
    return "m4a";
  if (mt.includes("mpeg") || mt.includes("mp3")) return "mp3";
  if (mt.includes("ogg")) return "ogg";
  if (mt.includes("wav")) return "wav";
  return "webm";
}

export function formatElapsed(ms) {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function shouldRotateChunk(
  elapsedSinceChunkStart,
  maxMs = CHUNK_ROTATE_MS,
) {
  return Number(elapsedSinceChunkStart) >= Number(maxMs);
}

export function photoOffsetFromStart(startedAt, nowMs = Date.now()) {
  const started = startedAt instanceof Date ? startedAt : new Date(startedAt);
  if (Number.isNaN(started.getTime())) return 0;
  return Math.max(0, nowMs - started.getTime());
}

export function statusLabelKey(status) {
  if (status === "processing") return "Meetings.statusProcessing";
  if (status === "ready") return "Meetings.statusReady";
  if (status === "failed") return "Meetings.statusFailed";
  return "Meetings.statusRecording";
}

export function normalizeUserOptions(rows = []) {
  const seen = new Set();
  return rows
    .map((row) => ({
      id: Number(row?.id ?? row?.userId),
      name: String(row?.username || row?.name || "").trim(),
    }))
    .filter((row) => {
      if (!Number.isFinite(row.id) || row.id <= 0 || !row.name) return false;
      if (seen.has(row.id)) return false;
      seen.add(row.id);
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name, "es"));
}

export function toDateInputValue(value, now = new Date()) {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const parsed = value instanceof Date ? value : new Date(value || now);
  const d = Number.isNaN(parsed.getTime()) ? now : parsed;
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export function normalizeContactOptions(rows = []) {
  const seen = new Set();
  return rows
    .map((row) => {
      const id = Number(row?.id ?? row?.contactId);
      const first = String(row?.firstName || "").trim();
      const last = String(row?.lastName || "").trim();
      const name = String(
        row?.name || `${first} ${last}`.trim() || row?.email || "",
      ).trim();
      return {
        id,
        name,
        email: String(row?.email || "").trim(),
        customerId: Number(row?.customerId) || null,
        customerName: String(row?.customerName || "").trim(),
      };
    })
    .filter((row) => {
      if (!Number.isFinite(row.id) || row.id <= 0 || !row.name) return false;
      if (seen.has(row.id)) return false;
      seen.add(row.id);
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name, "es"));
}

export function normalizeMarcaOptions(rows = []) {
  const seen = new Set();
  return rows
    .map((row) => ({
      id: row?.ID_MARCA ?? row?.id ?? null,
      name: String(row?.MARCA || row?.Marca || row?.name || "").trim(),
    }))
    .filter((row) => {
      if (!row.name) return false;
      const key = row.name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name, "es"));
}

export const MEETING_DELETE_USERNAMES = new Set(["miguel", "migue"]);
export const MEETING_DELETE_CONFIRM_WORD = "ELIMINAR";
export const MEETING_CONSENT_VERSION = "2026-09-14";
export const MEETING_MEDIA_RETENTION_DAYS = 60;
/** Aviso suave: conviene no pasarse; aún así se puede subir. */
export const MEETING_MEDIA_WARN_BYTES = 25 * 1024 * 1024;
/** Tope técnico del servidor. */
export const MEETING_MEDIA_MAX_BYTES = 100 * 1024 * 1024;

export function formatMeetingMediaMaxMb(maxBytes = MEETING_MEDIA_MAX_BYTES) {
  return Math.round(
    (Number(maxBytes) || MEETING_MEDIA_MAX_BYTES) / (1024 * 1024),
  );
}

export function formatMeetingMediaWarnMb(warnBytes = MEETING_MEDIA_WARN_BYTES) {
  return Math.round(
    (Number(warnBytes) || MEETING_MEDIA_WARN_BYTES) / (1024 * 1024),
  );
}

export function isMeetingMediaOversize(
  byteLength,
  maxBytes = MEETING_MEDIA_MAX_BYTES,
) {
  const size = Number(byteLength);
  const max = Number(maxBytes) || MEETING_MEDIA_MAX_BYTES;
  return Number.isFinite(size) && size > max;
}

export function shouldWarnMeetingMediaSize(
  byteLength,
  {
    warnBytes = MEETING_MEDIA_WARN_BYTES,
    maxBytes = MEETING_MEDIA_MAX_BYTES,
  } = {},
) {
  const size = Number(byteLength);
  if (!Number.isFinite(size)) return false;
  return size > warnBytes && size <= maxBytes;
}

function parseMeetingDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function mediaRetentionDeadline(
  startedAt,
  retentionDays = MEETING_MEDIA_RETENTION_DAYS,
) {
  const start = parseMeetingDate(startedAt);
  if (!start) return null;
  const days = Math.max(
    1,
    Number(retentionDays) || MEETING_MEDIA_RETENTION_DAYS,
  );
  return new Date(start.getTime() + days * 24 * 60 * 60 * 1000);
}

export function daysUntilMediaPurge(
  startedAt,
  {
    now = new Date(),
    retentionDays = MEETING_MEDIA_RETENTION_DAYS,
    mediaPurgedAt = null,
  } = {},
) {
  if (mediaPurgedAt) return 0;
  const deadline = mediaRetentionDeadline(startedAt, retentionDays);
  if (!deadline) return null;
  const nowDate = parseMeetingDate(now) || new Date();
  const ms = deadline.getTime() - nowDate.getTime();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

export function mediaDaysLeftFromSession(session, now = new Date()) {
  if (!session) return null;
  if (session.mediaPurged || session.mediaPurgedAt) return 0;
  if (session.mediaDaysLeft != null) return Number(session.mediaDaysLeft);
  return daysUntilMediaPurge(session.startedAt || session.createdAt, { now });
}

export function canDeleteMeetingFromToken(decoded = {}) {
  return MEETING_DELETE_USERNAMES.has(
    String(decoded?.username || "")
      .trim()
      .toLowerCase(),
  );
}

export function isMeetingDeleteConfirmText(value) {
  return (
    String(value || "")
      .trim()
      .toUpperCase() === MEETING_DELETE_CONFIRM_WORD
  );
}

export function isMeetingVideoMime(mimeType) {
  return String(mimeType || "")
    .trim()
    .toLowerCase()
    .startsWith("video/");
}

export function mediaKindFromMime(mimeType) {
  return isMeetingVideoMime(mimeType) ? "video" : "photo";
}

export function hasMeetingConsent(session) {
  if (!session) return false;
  if (session.hasConsent != null) return Boolean(session.hasConsent);
  return Boolean(
    session.consentClientsInformed &&
    session.consentRecordingAccepted &&
    session.consentAiAccepted &&
    session.consentAt,
  );
}

export function isMeetingConsentComplete(flags = {}) {
  return Boolean(
    flags.consentClientsInformed &&
    flags.consentRecordingAccepted &&
    flags.consentAiAccepted,
  );
}

/**
 * Extrae clips [Vídeo id=N @ …] de la transcripción ya guardada.
 * Sirve para mostrar audio de vídeos sin regenerar el resumen.
 */
export function extractVideoTranscriptSections(transcript) {
  const text = String(transcript || "");
  if (!text.trim()) return [];
  const re =
    /\[Vídeo id=(\d+)(?: @ ([^\]]+))?\]\s*([\s\S]*?)(?=\n\[Vídeo id=|\n*$)/gi;
  const out = [];
  let match;
  while ((match = re.exec(text)) !== null) {
    const body = String(match[3] || "")
      .replace(/\s+/g, " ")
      .trim();
    if (!body || /^\(sin voz|^\(no se pudo/i.test(body)) continue;
    const photoId = Number(match[1]);
    out.push({
      text: `Vídeo #${match[1]}${match[2] ? ` @ ${match[2]}` : ""}: ${body}`,
      photoIds: Number.isFinite(photoId) ? [photoId] : [],
      offsetMs: null,
    });
  }
  return out;
}

function itemPhotoIds(item) {
  if (Array.isArray(item?.photoIds) && item.photoIds.length) {
    return item.photoIds.map(Number).filter((n) => Number.isFinite(n));
  }
  if (item?.photoId != null) {
    const n = Number(item.photoId);
    return Number.isFinite(n) ? [n] : [];
  }
  return [];
}

function stripVideoLabelPrefix(text) {
  return String(text || "")
    .replace(/^V[ií]deo\s*#\d+(?:\s*@\s*[^\s:]+)?\s*:\s*/i, "")
    .trim();
}

function pushUnique(list, text) {
  const t = String(text || "").trim();
  if (!t) return;
  if (list.some((x) => x === t)) return;
  list.push(t);
}

function isVideoPhoto(photo) {
  if (!photo) return false;
  if (photo.kind === "video") return true;
  return mediaKindFromMime(photo.mimeType) === "video";
}

/**
 * Rangos temporales de cada tramo de audio (por seq).
 * Si falta durationMs, asume CHUNK_ROTATE_MS.
 */
export function audioChunkRanges(
  chunks = [],
  { defaultDurationMs = CHUNK_ROTATE_MS } = {},
) {
  let cursor = 0;
  return [...chunks]
    .sort((a, b) => Number(a.seq) - Number(b.seq))
    .map((c) => {
      const durationMs =
        Number(c.durationMs) > 0
          ? Number(c.durationMs)
          : Number(defaultDurationMs);
      const startMs = cursor;
      const endMs = cursor + durationMs;
      cursor = endMs;
      return {
        id: c.id,
        seq: c.seq,
        startMs,
        endMs,
        durationMs,
        fileUrl: c.fileUrl,
        transcriptText: String(c.transcriptText || "").trim(),
        hasTranscript: Boolean(
          c.hasTranscript || String(c.transcriptText || "").trim(),
        ),
      };
    });
}

/** Tramo de audio que cubre un offsetMs de sesión (foto tomada durante la grabación). */
export function findAudioChunkForOffset(offsetMs, ranges = []) {
  if (!ranges.length) return null;
  const ms = Math.max(0, Number(offsetMs) || 0);
  const hit = ranges.find((r) => ms >= r.startMs && ms < r.endMs);
  if (hit) return hit;
  if (ms >= ranges[ranges.length - 1].endMs) return ranges[ranges.length - 1];
  return ranges[0];
}

function nearestPhotoByOffset(photos, offsetMs) {
  const ms = Number(offsetMs);
  if (!Number.isFinite(ms) || !photos?.length) return null;
  let best = null;
  let bestDist = Infinity;
  for (const p of photos) {
    const d = Math.abs((Number(p.offsetMs) || 0) - ms);
    if (d < bestDist) {
      bestDist = d;
      best = p;
    }
  }
  // Solo acoplar si está a menos de 3 min del punto del acta
  if (best && bestDist <= 3 * 60 * 1000) return best;
  return null;
}

/**
 * Agrupa notas del resumen por id de foto/vídeo.
 * En fotos fijas NO se usan visualNotes (“lo que se ve”): solo audio acoplado
 * (fromVideos / puntos del acta con photoIds u offsetMs cercano).
 * @returns {Map<number, { visual: string[], spoken: string[], linked: string[] }>}
 */
export function notesByPhotoId(summary, transcript, { photos = [] } = {}) {
  const map = new Map();
  const photosById = new Map((photos || []).map((p) => [Number(p.id), p]));
  const ensure = (id) => {
    const n = Number(id);
    if (!Number.isFinite(n)) return null;
    if (!map.has(n)) {
      map.set(n, { visual: [], spoken: [], linked: [] });
    }
    return map.get(n);
  };

  const notes = Array.isArray(summary?.visualNotes) ? summary.visualNotes : [];
  for (const note of notes) {
    const ids = itemPhotoIds(note);
    const text = String(note?.text || "").trim();
    if (!text || !ids.length) continue;
    for (const id of ids) {
      const photo = photosById.get(Number(id));
      // Sin catálogo de fotos: conservar (tests / legacy). Con catálogo: solo vídeos.
      if (photosById.size && photo && !isVideoPhoto(photo)) continue;
      if (photosById.size && !photo) continue;
      const bucket = ensure(id);
      if (bucket) pushUnique(bucket.visual, text);
    }
  }

  let spokenItems = Array.isArray(summary?.fromVideos)
    ? summary.fromVideos
    : [];
  if (!spokenItems.length) {
    spokenItems = extractVideoTranscriptSections(
      transcript ?? summary?.fullTranscript,
    );
  }
  for (const item of spokenItems) {
    const ids = itemPhotoIds(item);
    const text = stripVideoLabelPrefix(item?.text);
    if (!text || !ids.length) continue;
    for (const id of ids) {
      const bucket = ensure(id);
      if (bucket) pushUnique(bucket.spoken, text);
    }
  }

  const linkedSections = [
    ...(Array.isArray(summary?.forDesign) ? summary.forDesign : []),
    ...(Array.isArray(summary?.forSales) ? summary.forSales : []),
    ...(Array.isArray(summary?.decisions) ? summary.decisions : []),
    ...(Array.isArray(summary?.openQuestions) ? summary.openQuestions : []),
  ];
  for (const item of linkedSections) {
    const ids = itemPhotoIds(item);
    const text = String(item?.text || "").trim();
    if (!text) continue;
    if (ids.length) {
      for (const id of ids) {
        const bucket = ensure(id);
        if (bucket) pushUnique(bucket.linked, text);
      }
      continue;
    }
    if (item.offsetMs != null && photos.length) {
      const near = nearestPhotoByOffset(photos, item.offsetMs);
      if (near) {
        const bucket = ensure(near.id);
        if (bucket) pushUnique(bucket.linked, text);
      }
    }
  }

  return map;
}

/**
 * Acopla cada foto (no vídeo) al tramo de audio por offsetMs.
 * @returns {Map<number, { chunk: object, spoken: string[] }>}
 */
export function audioCouplingByPhotoId(photos = [], audioChunks = []) {
  const ranges = audioChunkRanges(audioChunks);
  const out = new Map();
  if (!ranges.length) return out;
  for (const photo of photos || []) {
    if (isVideoPhoto(photo)) continue;
    const chunk = findAudioChunkForOffset(photo.offsetMs, ranges);
    if (!chunk) continue;
    const spoken = [];
    if (chunk.transcriptText) pushUnique(spoken, chunk.transcriptText);
    out.set(Number(photo.id), { chunk, spoken });
  }
  return out;
}

export const MEETING_UNGROUPED_ID = "__ungrouped__";

export function newMeetingMediaGroupId() {
  return `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function sanitizeMeetingMediaGroups(input, availablePhotoIds = []) {
  const known = new Set(
    (availablePhotoIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id)),
  );
  const seen = new Set();
  const groups = [];
  const list = Array.isArray(input) ? input : [];
  for (let i = 0; i < list.length; i += 1) {
    const g = list[i] || {};
    let id = String(g.id || "")
      .trim()
      .slice(0, 40);
    if (!id || id === MEETING_UNGROUPED_ID) id = newMeetingMediaGroupId();
    const label = String(g.label || "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 120);
    const mediaIds = [];
    for (const raw of Array.isArray(g.mediaIds) ? g.mediaIds : []) {
      const mid = Number(raw);
      if (!Number.isFinite(mid) || !known.has(mid) || seen.has(mid)) continue;
      seen.add(mid);
      mediaIds.push(mid);
    }
    if (!mediaIds.length && !label) {
      groups.push({
        id,
        label: `Grupo ${groups.length + 1}`,
        mediaIds: [],
      });
      continue;
    }
    groups.push({ id, label, mediaIds });
  }
  return groups;
}

/** Columnas de tablero: grupos + sin agrupar. */
export function buildMeetingMediaBoard(photos = [], groups = []) {
  const byId = new Map((photos || []).map((p) => [Number(p.id), p]));
  const used = new Set();
  const columns = [];
  for (const g of groups || []) {
    const items = [];
    for (const id of g.mediaIds || []) {
      const p = byId.get(Number(id));
      if (!p || used.has(Number(id))) continue;
      items.push(p);
      used.add(Number(id));
    }
    columns.push({
      id: String(g.id),
      label: String(g.label || ""),
      photos: items,
    });
  }
  const ungrouped = (photos || []).filter((p) => !used.has(Number(p.id)));
  columns.push({
    id: MEETING_UNGROUPED_ID,
    label: "",
    photos: ungrouped,
    ungrouped: true,
  });
  return columns;
}

/** Solo se puede borrar un grupo vacío (no el sin agrupar). */
export function canRemoveMeetingMediaGroup(column) {
  if (!column || column.ungrouped) return false;
  if (column.id === MEETING_UNGROUPED_ID) return false;
  return !(Array.isArray(column.photos) && column.photos.length > 0);
}

export function mediaGroupsFromBoard(columns) {
  return (columns || [])
    .filter((c) => c && c.id !== MEETING_UNGROUPED_ID && !c.ungrouped)
    .map((c) => ({
      id: String(c.id),
      label: String(c.label || "").trim(),
      mediaIds: (c.photos || []).map((p) => Number(p.id)),
    }));
  // Conservar grupos vacíos (para poder soltar media después de "Nuevo grupo")
}

/**
 * Varias piezas → deben pertenecer al mismo grupo nombrado.
 * Una sola pieza (aunque esté sin agrupar) → OK.
 */
export function canCreateSampleFromSelection(
  selectedPhotoIds = [],
  mediaGroups = [],
) {
  const ids = [
    ...new Set(
      (selectedPhotoIds || [])
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n) && n > 0),
    ),
  ];
  if (ids.length <= 1) return { ok: true };

  const photoToGroup = new Map();
  for (const g of mediaGroups || []) {
    const gid = String(g?.id || "").trim();
    if (!gid || gid === MEETING_UNGROUPED_ID) continue;
    for (const raw of g.mediaIds || []) {
      const mid = Number(raw);
      if (Number.isFinite(mid)) photoToGroup.set(mid, gid);
    }
  }

  const groupKeys = ids.map((id) => photoToGroup.get(id) || null);
  const first = groupKeys[0];
  if (!first || groupKeys.some((g) => g !== first)) {
    return { ok: false, reason: "need_group" };
  }
  return { ok: true, groupId: first };
}

export function applyMeetingMediaDrag(columns, result) {
  const { destination, source } = result || {};
  if (!destination) return null;
  if (
    destination.droppableId === source.droppableId &&
    destination.index === source.index
  ) {
    return null;
  }
  const next = (columns || []).map((c) => ({
    ...c,
    photos: [...(c.photos || [])],
  }));
  const from = next.find((c) => c.id === source.droppableId);
  const to = next.find((c) => c.id === destination.droppableId);
  if (!from || !to) return null;
  const [moved] = from.photos.splice(source.index, 1);
  if (!moved) return null;
  to.photos.splice(destination.index, 0, moved);
  return next;
}

/** Párrafo corto del acta (sin dumps de vídeo / contadores legacy). */
export function shortMeetingBlurb(rawSummary, headline = "") {
  let text = String(rawSummary || "").trim();
  if (text) {
    text = text
      .split(/\n(?=Desde vídeos|Cubierto:|Visual:)/i)[0]
      .replace(/\n*Cubierto:.*$/gim, "")
      .trim();
  }
  if (!text) text = String(headline || "").trim();
  if (text.length > 480) text = `${text.slice(0, 477).trim()}…`;
  return text;
}

/** Acta plana en es|en. El DTO trae campos ES en raíz y opcionalmente `en`. */

/** Ids de MeetingPhoto válidos (enteros positivos), sin duplicados, orden estable. */
export function sanitizeMeetingImportPhotoIds(ids = []) {
  const seen = new Set();
  const out = [];
  for (const raw of ids || []) {
    const n = Number(raw);
    if (!Number.isInteger(n) || n <= 0 || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

/**
 * Concatena Notas STAGE de piezas seleccionadas para NOTAS / email de petición.
 * photos: [{ id, kind?, mimeType?, teamNote? }] en el orden deseado.
 */
export function buildMeetingSampleNotes(photos = [], { labels } = {}) {
  const fotoLabel = labels?.photo || "Foto";
  const videoLabel = labels?.video || "Vídeo";
  const lines = [];
  for (const photo of photos || []) {
    const note = String(photo?.teamNote || "").trim();
    if (!note) continue;
    const isVideo =
      photo.kind === "video" || mediaKindFromMime(photo.mimeType) === "video";
    const label = isVideo ? videoLabel : fotoLabel;
    lines.push(`${label} #${photo.id}:\n${note}`);
  }
  return lines.join("\n\n");
}

export function pickMeetingSummaryLocale(summary, locale = "es") {
  if (!summary || typeof summary !== "object") {
    return {
      headline: "",
      visualNotes: [],
      fromVideos: [],
      forDesign: [],
      forSales: [],
      decisions: [],
      openQuestions: [],
      rawSummary: "",
      locale: "es",
      hasEn: false,
    };
  }
  const hasEn = Boolean(summary.en && typeof summary.en === "object");
  if (locale === "en" && hasEn) {
    const en = summary.en;
    return {
      headline: en.headline || "",
      visualNotes: Array.isArray(en.visualNotes) ? en.visualNotes : [],
      fromVideos: Array.isArray(en.fromVideos) ? en.fromVideos : [],
      forDesign: Array.isArray(en.forDesign) ? en.forDesign : [],
      forSales: Array.isArray(en.forSales) ? en.forSales : [],
      decisions: Array.isArray(en.decisions) ? en.decisions : [],
      openQuestions: Array.isArray(en.openQuestions) ? en.openQuestions : [],
      rawSummary: en.rawSummary || "",
      locale: "en",
      hasEn: true,
    };
  }
  return {
    headline: summary.headline || "",
    visualNotes: Array.isArray(summary.visualNotes) ? summary.visualNotes : [],
    fromVideos: Array.isArray(summary.fromVideos) ? summary.fromVideos : [],
    forDesign: Array.isArray(summary.forDesign) ? summary.forDesign : [],
    forSales: Array.isArray(summary.forSales) ? summary.forSales : [],
    decisions: Array.isArray(summary.decisions) ? summary.decisions : [],
    openQuestions: Array.isArray(summary.openQuestions)
      ? summary.openQuestions
      : [],
    rawSummary: summary.rawSummary || "",
    locale: "es",
    hasEn,
  };
}
